import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Edit, Trash2, Eye, ExternalLink } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const emptyForm = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  cover_image: '',
  author_name: 'TesisKirala Editör',
  meta_title: '',
  meta_description: '',
  keywords: '',
  city: '',
  facility_type: '',
  status: 'published',
};

function slugify(text) {
  const map = { 'ç':'c','Ç':'c','ğ':'g','Ğ':'g','ı':'i','İ':'i','ö':'o','Ö':'o','ş':'s','Ş':'s','ü':'u','Ü':'u' };
  return (text || '')
    .split('').map(c => map[c] || c).join('')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`, { withCredentials: true });
      setPosts(data || []);
    } catch (e) {
      console.error(e);
      toast.error('Yazılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      slug: p.slug || '',
      title: p.title || '',
      excerpt: p.excerpt || '',
      content: p.content || '',
      cover_image: p.cover_image || '',
      author_name: p.author_name || 'TesisKirala Editör',
      meta_title: p.meta_title || '',
      meta_description: p.meta_description || '',
      keywords: (p.keywords || []).join(', '),
      city: p.city || '',
      facility_type: p.facility_type || '',
      status: p.status || 'published',
    });
    setShowDialog(true);
  };

  const handleTitleBlur = () => {
    if (!editing && !form.slug && form.title) {
      setForm(f => ({ ...f, slug: slugify(f.title) }));
    }
  };

  const handleSave = async () => {
    if (!form.slug || !form.title || !form.content) {
      toast.error('Slug, başlık ve içerik zorunlu');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords ? form.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        city: form.city || null,
        facility_type: form.facility_type || null,
      };
      if (editing) {
        await axios.put(`${API}/admin/blog/posts/${editing.post_id}`, payload, { withCredentials: true });
        toast.success('Yazı güncellendi');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { withCredentials: true });
        toast.success('Yazı oluşturuldu');
      }
      setShowDialog(false);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/admin/blog/posts/${deletingId}`, { withCredentials: true });
      toast.success('Yazı silindi');
      setShowDelete(false);
      setDeletingId(null);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silme başarısız');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog Yazıları</h1>
          <p className="text-sm text-slate-600 mt-1">Toplam {posts.length} yazı</p>
        </div>
        <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-add-blog-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Yazı Ekle
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-20 flex justify-center">
              <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="py-20 text-center text-slate-500">Henüz yazı yok. Sağ üstten ekleyin.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlık</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Şehir / Tür</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Görüntülenme</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.post_id} data-testid={`blog-row-${p.slug}`}>
                    <TableCell className="font-medium text-slate-900">{p.title}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{p.slug}</TableCell>
                    <TableCell className="text-xs">
                      {p.city || '-'}{p.facility_type ? ` / ${p.facility_type}` : ''}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 text-xs rounded-full ${p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {p.status === 'published' ? 'Yayında' : 'Taslak'}
                      </span>
                    </TableCell>
                    <TableCell><span className="inline-flex items-center gap-1 text-sm text-slate-600"><Eye className="w-3 h-3" /> {p.view_count || 0}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${p.slug}`, '_blank')} title="Görüntüle">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} title="Düzenle" data-testid={`blog-edit-${p.slug}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setDeletingId(p.post_id); setShowDelete(true); }} className="text-red-600 hover:bg-red-50" title="Sil">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Yazıyı Düzenle' : 'Yeni Blog Yazısı'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Başlık *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} onBlur={handleTitleBlur} placeholder="örn: İstanbul'da Halı Saha Kiralama Rehberi" data-testid="blog-form-title" />
            </div>
            <div>
              <Label>Slug * (URL)</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} placeholder="istanbul-hali-saha-kiralama-rehberi" data-testid="blog-form-slug" />
              <p className="text-xs text-slate-500 mt-1">URL: /blog/{form.slug || 'slug'}</p>
            </div>
            <div>
              <Label>Özet</Label>
              <Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} placeholder="Yazının kısa özeti (kart ve meta açıklamasında kullanılır)" />
            </div>
            <div>
              <Label>İçerik * (HTML destekli)</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={14} placeholder="Yazının tam içeriği. HTML etiketleri kullanabilirsiniz: <h2>, <p>, <ul>, <a> vb." className="font-mono text-sm" data-testid="blog-form-content" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kapak Görseli (URL)</Label>
                <Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://..." />
              </div>
              <div>
                <Label>Yazar</Label>
                <Input value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} />
              </div>
              <div>
                <Label>Şehir (opsiyonel)</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="İstanbul" />
              </div>
              <div>
                <Label>Tesis Türü (opsiyonel)</Label>
                <Select value={form.facility_type || 'none'} onValueChange={(v) => setForm({ ...form, facility_type: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">- Yok -</SelectItem>
                    <SelectItem value="hali_saha">Halı Saha</SelectItem>
                    <SelectItem value="spor_salonu">Spor Salonu</SelectItem>
                    <SelectItem value="kapali_spor_salonu">Kapalı Spor Salonu</SelectItem>
                    <SelectItem value="voleybol_sahasi">Voleybol Sahası</SelectItem>
                    <SelectItem value="basketbol_sahasi">Basketbol Sahası</SelectItem>
                    <SelectItem value="tenis_kortu">Tenis Kortu</SelectItem>
                    <SelectItem value="yuzme_havuzu">Yüzme Havuzu</SelectItem>
                    <SelectItem value="plaj_voleybolu">Plaj Voleybolu Sahası</SelectItem>
                    <SelectItem value="masa_tenisi">Masa Tenisi Salonu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Anahtar Kelimeler (virgülle ayırın)</Label>
                <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="halı saha, istanbul, rezervasyon" />
              </div>
              <div className="col-span-2">
                <Label>Meta Başlık (SEO)</Label>
                <Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} placeholder="Boş bırakırsanız başlık kullanılır" />
              </div>
              <div className="col-span-2">
                <Label>Meta Açıklama (SEO)</Label>
                <Textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} placeholder="Boş bırakırsanız özet kullanılır (max 160 karakter)" />
              </div>
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Yayında</SelectItem>
                    <SelectItem value="draft">Taslak</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>İptal</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="blog-form-save-btn">
              {saving ? 'Kaydediliyor...' : (editing ? 'Güncelle' : 'Yayınla')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yazıyı sil?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 py-2">Bu işlem geri alınamaz.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>İptal</Button>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
